import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/MembershipPage.css';
import Header from '../Header';
import Footer from '../Footer';
import { PUBLIC_URLS } from '../config/apiConfig';

// Use local images from membership folder
import MembershipImage from '../../assets/images/membership/membership-hero.png';
import PrimaryMemberImage from '../../assets/images/membership/primary-member.png';
import AssociateMemberImage from '../../assets/images/membership/associate-member.png';
import StudentMemberImage from '../../assets/images/membership/student-member.png';
import FinancialCollegiumImage from '../../assets/images/membership/financial-collegium.png';
import AcademicCollegiumImage from '../../assets/images/membership/academic-collegium.png';
import InstitutionalCollegiumImage from '../../assets/images/membership/institutional-collegium.png';
import AdvantagesImage from '../../assets/images/membership/advantages.png';

// We'll use CSS background colors instead of images
function MembershipPage() {
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [renewalStep, setRenewalStep] = useState(1); // Step 1: Identification, Step 2: Renewal details
  const [identificationMethod, setIdentificationMethod] = useState('phone');
  const [identificationValue, setIdentificationValue] = useState('');
  const [memberDetails, setMemberDetails] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Add new state for payment status
  const [paymentResult, setPaymentResult] = useState(null); // null, 'success', 'cancelled', 'failed'
  
  // Modify these refs for better focus management
  const inputRef = useRef(null);
  const cursorPositionRef = useRef(null);
  
  // Add reference to the advantages section
  const advantagesSectionRef = useRef(null);
  // membership
  const [plans, setPlans] = useState([]);

  
  // Scroll function to handle smooth scrolling to a section
  const scrollToSection = (ref) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Replace the two existing useEffect hooks with this single more effective one
  useEffect(() => {
  // Only run this effect when the modal is shown
  if (showRenewalModal && renewalStep === 1 && inputRef.current) {
    // Set focus on the current input element
    const focusInput = () => {
      if (inputRef.current) {
        inputRef.current.focus();
        
        // Only attempt to set selection range for input types that support it
        const inputType = inputRef.current.type;
        if (cursorPositionRef.current !== null && 
            (inputType === 'text' || inputType === 'tel' || inputType === 'search' || 
             inputType === 'url' || inputType === 'password')) {
          try {
            inputRef.current.setSelectionRange(
              cursorPositionRef.current,
              cursorPositionRef.current
            );
          } catch (error) {
            console.log('Selection range not supported for this input type');
          }
        }
      }
    };
    
    // Schedule focusing after the render cycle completes
    setTimeout(focusInput, 10);
  }
}, [showRenewalModal, renewalStep, identificationValue, identificationMethod]);

  useEffect(() => {
    fetch(PUBLIC_URLS.MEMBERSHIP_PLANS)
      .then(res => res.json())
      .then(data => {
        setPlans(data.plans);
      })
      .catch(err => console.error("Failed to load plans", err));
  }, []);

  const getPrice = (planName) => {
    const plan = plans.find(p => p.name === planName);
    return plan ? `₹${plan.price}` : '₹0';
  };



  
  const openRenewalModal = () => {
    setShowRenewalModal(true);
    setRenewalStep(1);
    setIdentificationValue('');
    setError('');
    setMemberDetails(null);
  };

  const closeRenewalModal = () => {
    setShowRenewalModal(false);
  };

  const handleIdentificationChange = (e) => {
    setIdentificationMethod(e.target.value);
    setIdentificationValue('');
    setError('');
  };

// Update the handleInputChange function to only store cursor position for supported input types
const handleInputChange = (e) => {
  // Store the current cursor position before React updates
  if (inputRef.current) {
    const inputType = inputRef.current.type;
    if (inputType === 'text' || inputType === 'tel' || inputType === 'search' || 
        inputType === 'url' || inputType === 'password') {
      cursorPositionRef.current = e.target.selectionStart;
    } else {
      cursorPositionRef.current = null;
    }
  }
  
  // Update the state without preventing default
  setIdentificationValue(e.target.value);
  setError('');
};

  const fetchMemberDetails = (e) => {
    e.preventDefault();
    
    // Validate input
    if (!identificationValue.trim()) {
      setError('Please enter your identification details.');
      return;
    }
    
    // Additional input validation based on identification method
    if (identificationMethod === 'phone' && !/^\d{10}$/.test(identificationValue.trim())) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    
    if (identificationMethod === 'email' && !/\S+@\S+\.\S+/.test(identificationValue.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    
    if (identificationMethod === 'bvaId' && !/^BVA\d+$/.test(identificationValue.trim())) {
      setError('Please enter a valid BVA ID (e.g., BVA20230401).');
      return;
    }
    
    // Show loading state
    setIsLoading(true);
    
    // Make API call to fetch member details using endpoint from config
    fetch(PUBLIC_URLS.MEMBER_LOOKUP, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        method: identificationMethod,
        value: identificationValue.trim()
      })
    })
    .then(response => {
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('No member found with these details. Please check and try again.');
        }
        throw new Error('Failed to find member details. Please try again later.');
      }
      return response.json();
    })
    .then(data => {
      // Process the API response - no discount calculation now
      setMemberDetails({
        memberId: data.memberId,
        name: `${data.firstName} ${data.lastName}`,
        membershipType: data.membershipType,
        validUntil: data.membershipExpiry,
        renewalEligibleFrom: data.renewalEligibleFrom,
        amount: data.renewalPrice // Just use the renewal price
      });
      setRenewalStep(2);
      setIsLoading(false);
      setError('');
    })
    .catch(error => {
      setError(error.message);
      setIsLoading(false);
    });
  };
  
  const handleRenewal = async () => {
    try {
      setIsLoading(true);
      setPaymentResult(null); // Reset payment result state
      
      // Get order details from server using endpoint from config
      const response = await fetch(PUBLIC_URLS.MEMBER_RENEWAL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          method: identificationMethod,
          value: identificationValue.trim()
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to initiate renewal payment');
      }
      
      const orderData = await response.json();
      
      // Initialize Razorpay payment
      const options = {
        key: orderData.razorpay_key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Bangalore Valuers Association',
        description: 'Membership Renewal',
        order_id: orderData.razorpay_order_id,
        handler: async function (response) {
          try {
            // Show loading state while verifying payment
            setIsLoading(true);
            
            // Prepare verification payload
            const verificationPayload = {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            };

            // Verify payment with server using the same renewal endpoint
            const verifyResponse = await fetch(PUBLIC_URLS.MEMBER_RENEWAL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(verificationPayload),
            });
            
            if (!verifyResponse.ok) {
              throw new Error('Payment verification failed');
            }
            
            const verifyData = await verifyResponse.json();
            
            if (verifyData.success) {
              setPaymentResult('success');
              // Don't close modal immediately, show success message instead
            } else {
              setPaymentResult('failed');
              setError('Payment could not be verified. Please contact support.');
            }
          } catch (err) {
            setPaymentResult('failed');
            setError('Payment verification failed. Please contact support.');
          } finally {
            setIsLoading(false);
          }
        },
        modal: {
          ondismiss: function() {
            // Handle the case when user closes the Razorpay modal without completing payment
            setIsLoading(false);
            setPaymentResult('cancelled');
          }
        },
        prefill: {
          name: memberDetails.name,
          email: identificationMethod === 'email' ? identificationValue : '',
        },
        theme: {
          color: '#002147'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      setError(error.message);
      setIsLoading(false);
      setPaymentResult('failed');
    }
  };

  // Function to handle modal close with payment result
  const handleModalClose = () => {
    // If payment was successful, show an alert before closing
    if (paymentResult === 'success') {
      alert('Membership renewed successfully! You will receive a confirmation email shortly.');
    }
    closeRenewalModal();
  };

  // Renewal modal component
  const RenewalModal = () => {
    if (!showRenewalModal) return null;
    
    return (
      <div className="renewal-modal-overlay">
        <div className="renewal-modal">
          <div className="renewal-modal-header">
            <h3>Membership Renewal</h3>
            <button className="close-button" onClick={handleModalClose}>×</button>
          </div>
          
          <div className="renewal-modal-content">
            {isLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Processing your request...</p>
              </div>
            ) : paymentResult === 'success' ? (
              <div className="payment-result payment-success">
                <div className="payment-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <h4>Payment Successful!</h4>
                <p>Your membership has been renewed successfully. An email confirmation has been sent to your registered email address.</p>
                <div className="payment-result-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={handleModalClose}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : paymentResult === 'cancelled' ? (
              <div className="payment-result payment-cancelled">
                <div className="payment-icon">
                  <i className="fas fa-times-circle"></i>
                </div>
                <h4>Payment Cancelled</h4>
                <p>You cancelled the payment process. No changes have been made to your membership.</p>
                <div className="payment-result-actions">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setPaymentResult(null)} // Go back to renewal details
                  >
                    Try Again
                  </button>
                  <button 
                    className="btn btn-text"
                    onClick={handleModalClose}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : paymentResult === 'failed' ? (
              <div className="payment-result payment-failed">
                <div className="payment-icon">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <h4>Payment Failed</h4>
                <p>There was a problem processing your payment: {error}</p>
                <div className="payment-result-actions">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setPaymentResult(null)} // Go back to renewal details
                  >
                    Try Again
                  </button>
                  <button 
                    className="btn btn-text"
                    onClick={handleModalClose}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : renewalStep === 1 ? (
              <div className="identification-form">
                <h4>Identify yourself</h4>
                <p>Please provide your details to fetch your membership information.</p>
                
                <form onSubmit={fetchMemberDetails}>
                  <div className="form-group">
                    <div className="radio-group">
                      <label>
                        <input 
                          type="radio" 
                          name="identificationMethod" 
                          value="phone" 
                          checked={identificationMethod === 'phone'}
                          onChange={handleIdentificationChange}
                        />
                        Phone Number
                      </label>
                      <label>
                        <input 
                          type="radio" 
                          name="identificationMethod" 
                          value="email" 
                          checked={identificationMethod === 'email'}
                          onChange={handleIdentificationChange}
                        />
                        Email
                      </label>
                      <label>
                        <input 
                          type="radio" 
                          name="identificationMethod" 
                          value="bvaId" 
                          checked={identificationMethod === 'bvaId'}
                          onChange={handleIdentificationChange}
                        />
                        BVA ID
                      </label>
                    </div>
                    
                    <div className="input-group">
                      {identificationMethod === 'phone' && (
                        <input 
                          type="tel" 
                          placeholder="Enter your phone number" 
                          value={identificationValue}
                          onChange={handleInputChange}
                          pattern="[0-9]{10}"
                          title="Please enter a valid 10-digit phone number"
                          ref={inputRef} // Use single ref for all input types
                        />
                      )}
                      
                      {identificationMethod === 'email' && (
                        <input 
                          type="email" 
                          placeholder="Enter your email address" 
                          value={identificationValue}
                          onChange={handleInputChange}
                          ref={inputRef} // Use single ref for all input types
                        />
                      )}
                      
                      {identificationMethod === 'bvaId' && (
                        <input 
                          type="text" 
                          placeholder="Enter your BVA ID (e.g., BVA20230401)" 
                          value={identificationValue}
                          onChange={handleInputChange}
                          ref={inputRef} // Use single ref for all input types
                        />
                      )}
                    </div>
                    
                    {error && <p className="error-message">{error}</p>}
                    
                    <button type="submit" className="btn btn-primary">Fetch My Details</button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="renewal-details">
                <div className="member-details">
                  <h4>Membership Information</h4>
                  <div className="details-row">
                    <span>Member ID:</span>
                    <span>{memberDetails?.memberId}</span>
                  </div>
                  <div className="details-row">
                    <span>Name:</span>
                    <span>{memberDetails?.name}</span>
                  </div>
                  <div className="details-row">
                    <span>Membership Type:</span>
                    <span>{memberDetails?.membershipType}</span>
                  </div>
                  <div className="details-row highlight">
                    <span>Valid Until:</span>
                    <span>{new Date(memberDetails?.validUntil).toLocaleDateString('en-IN')}</span>
                  </div>
                  <div className="details-row highlight">
                    <span>Renewal Message:</span>
                    <span>Your membership is valid until {new Date(memberDetails?.validUntil).toLocaleDateString('en-IN')}. 
                    You can renew from {new Date(memberDetails?.renewalEligibleFrom).toLocaleDateString('en-IN')}.</span>
                  </div>
                </div>
                
                <div className="renewal-pricing">
                  <h4>Renewal Payment</h4>
                  <div className="price-row">
                    <span>Renewal Price:</span>
                    <span>₹{memberDetails?.amount}</span>
                  </div>
                  <button 
                    className="btn btn-primary btn-renew" 
                    onClick={handleRenewal}
                  >
                    Proceed to Payment
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="membership-page">
      {/* Payment Verification Loading Overlay */}
      {isLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            border: '6px solid rgba(255, 255, 255, 0.3)',
            borderTop: '6px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            color: 'white',
            fontSize: '1.2rem',
            marginTop: '1.5rem',
            fontWeight: '500'
          }}>
            Verifying your payment...
          </p>
          <p style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '0.9rem',
            marginTop: '0.5rem'
          }}>
            Please wait, this may take a few moments
          </p>
        </div>
      )}
      
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      {/* Header/Navigation */}
      <Header />

      {/* Main Membership Section */}
      <section className="membership-hero">
        <div className="membership-hero-image">
          <img src={MembershipImage} alt="BVA Membership" style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: 'relative',
            zIndex: 2
          }} />  
          <div className="image-attribution">Bangalore Valuers' Association</div>
        </div>
        <div className="membership-hero-content">
          <h2>OUR MEMBERSHIP</h2>
          <h3>Exceptional Value, Unmatched Benefits</h3>
          <p>
            The Bangalore Valuers' Association (BVA) is meant primarily for valuers in Asset Class  - Securities and Financial Assets. The membership is open to all across India and is not restricted to members from Bangalore. BVA conducts webinars usually through zoom online platform twice a month (Second and fourth Saturdays) and also conducts workshops in physical mode in various parts of India on various areas of valuation. Industry professionals / eminent speakers share their knowledge and experience with members in these workshops.
          </p>
          <div className="hero-cta">
            <button 
              className="btn btn-secondary" 
              onClick={() => scrollToSection(advantagesSectionRef)}
            >
              Learn More
            </button>
            <Link to="/register" className="btn" style={{backgroundColor: '#f7c936', textDecoration: 'none', display: 'inline-block', textAlign: 'center'}}>Join Now</Link>
          </div>
        </div>
      </section>

      {/* Advantages Section - add ref here */}
      <section className="advantages-section" ref={advantagesSectionRef}>
        <div className="advantages-container">
          <div className="advantages-text">
            <h2>ADVANTAGES OF MEMBERSHIP</h2>
            
            <div className="advantages-content">
              <div className="advantage-item">
                <div className="advantage-icon">
                  <i className="fas fa-graduation-cap"></i>
                </div>
                <div className="advantage-text">
                  <h4>Professional Development</h4>
                  <p>
                    Enhance professional knowledge by attending workshops and seminars with experienced industry professionals who share their insights.
                  </p>
                </div>
              </div>
              
              <div className="advantage-item">
                <div className="advantage-icon">
                  <i className="fas fa-handshake"></i>
                </div>
                <div className="advantage-text">
                  <h4>Networking Opportunities</h4>
                  <p>
                    BVA provides a platform for interaction and networking with fellow valuers across the country.
                  </p>
                </div>
              </div>
              
              <div className="advantage-item">
                <div className="advantage-icon">
                  <i className="fas fa-video"></i>
                </div>
                <div className="advantage-text">
                  <h4>Free Webinar Access</h4>
                  <p>
                    Members are entitled to attend the fortnightly webinars free of cost.
                  </p>
                </div>
              </div>
              
              <div className="advantage-item">
                <div className="advantage-icon">
                  <i className="fas fa-tags"></i>
                </div>
                <div className="advantage-text">
                  <h4>Discounted Programs</h4>
                  <p>
                    Members shall be entitled to attend other programs organised by BVA at a concessional cost compared to non-members.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="advantages-image">
            <img src={AdvantagesImage} alt="BVA Advantages" style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              position: 'relative',
              zIndex: 2,
              borderRadius: '8px'
            }} />
            <div className="image-attribution">Bangalore Valuers' Association</div>
          </div>
        </div>

        <div className="existing-member">
          <h3>ALREADY A MEMBER?</h3>
          <button 
            className="btn btn-primary btn-highlight" 
            onClick={openRenewalModal}
          >
            RENEW MEMBERSHIP
          </button>
        </div>
      </section>

      {/* Membership Plans Section */}
      <section className="membership-plans-section">
        <h2>MEMBERSHIP PLANS</h2>
        <p className="plans-description">
          Choose the membership plan that best suits your professional needs and career stage. Each plan offers unique benefits tailored to different roles in the valuation industry.
        </p>
        
        <div className="membership-cards">
          <div className="membership-card">
            <div className="card-image">
              <img src={PrimaryMemberImage} alt="Primary Membership" style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }} />
              <div className="card-overlay"></div>
              <div className="card-icon">
                <i className="fas fa-user-tie"></i>
              </div>
            </div>
            <h3>PRIMARY MEMBER</h3>
            <p className="card-description">
              If you are a Valuer registered with IBBI under the Asset class - Securities and Financial Assets (SFA), you are considered as a primary member of the Bangalore Valuers' Association (BVA).
            </p>
            <div className="price">{getPrice("PRIMARY MEMBER")}</div>
            <Link to="/register" className="btn btn-primary" style={{textDecoration: 'none', display: 'inline-block', textAlign: 'center', margin: '0 1.5rem 1.5rem'}}>GET MEMBERSHIP</Link>
          </div>
          
          <div className="membership-card">
            <div className="card-image">
              <img src={AssociateMemberImage} alt="Associate Membership" style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }} />
              <div className="card-overlay"></div>
              <div className="card-icon">
                <i className="fas fa-users"></i>
              </div>
            </div>
            <h3>ASSOCIATE MEMBER</h3>
            <p className="card-description">
             If you are Valuation enthusiast, L&B Valuer, P&M Valuer or a finance professional - CA, CS, CMA, CFA, MBA Finance or equivalent.
            </p>
            <div className="price">{getPrice("ASSOCIATE MEMBER")}</div>
            <Link to="/register" className="btn btn-primary" style={{textDecoration: 'none', display: 'inline-block', textAlign: 'center', margin: '0 1.5rem 1.5rem'}}>GET MEMBERSHIP</Link>
          </div>
          
          <div className="membership-card">
            <div className="card-image">
              <img src={StudentMemberImage} alt="Student Membership" style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }} />
              <div className="card-overlay"></div>
              <div className="card-icon">
                <i className="fas fa-user-graduate"></i>
              </div>
            </div>
            <h3>STUDENT MEMBERSHIP</h3>
            <p className="card-description">
              Student Membership is open to undergraduate students, as well as those enrolled in the Registered Valuer Course. This membership provides students with access to workshops, enabling them with access to resources that are essential for their academic and professional development.
            </p>
            <div className="price">{getPrice("STUDENT MEMBERSHIP")}</div>
            <Link to="/register" className="btn btn-primary" style={{textDecoration: 'none', display: 'inline-block', textAlign: 'center', margin: '0 1.5rem 1.5rem'}}>GET MEMBERSHIP</Link>
          </div>
          
          <div className="membership-card">
            <div className="card-image">
              <img src={FinancialCollegiumImage} alt="Financial Collegium" style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }} />
              <div className="card-overlay"></div>
              <div className="card-icon">
                <i className="fas fa-chart-line"></i>
              </div>
            </div>
            <h3>FINANCIAL COLLEGIUM</h3>
            <p className="card-description">
              The Financial Collegium is ideal for professionals from banks, NBFCs, VCs, PEs, and other financial entities. It offers access to valuation insights, industry updates, and networking with experts, helping members stay aligned with financial and investment-driven valuation practices. Each Collegium can register multiple individuals (up to 5 members)
            </p>
            <div className="price">{getPrice("FINANCIAL COLLEGIUM")}</div>
            <Link to="/register" className="btn btn-primary" style={{textDecoration: 'none', display: 'inline-block', textAlign: 'center', margin: '0 1.5rem 1.5rem'}}>GET MEMBERSHIP</Link>
          </div>
          
          <div className="membership-card">
            <div className="card-image">
              <img src={AcademicCollegiumImage} alt="Academic Collegium" style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }} />
              <div className="card-overlay"></div>
              <div className="card-icon">
                <i className="fas fa-school"></i>
              </div>
            </div>
            <h3>ACADEMIC COLLEGIUM</h3>
            <p className="card-description">
              The Academic Collegium is for professors, teachers, and researchers from B-Schools. It promotes academic-industry collaboration, offering access to valuation resources, expert interactions, and events. Members can contribute to research and stay updated on practical valuation trends.
            </p>
            <div className="price">{getPrice("ACADEMIC COLLEGIUM")}</div>
            <Link to="/register" className="btn btn-primary" style={{textDecoration: 'none', display: 'inline-block', textAlign: 'center', margin: '0 1.5rem 1.5rem'}}>GET MEMBERSHIP</Link>
          </div>
          
          <div className="membership-card">
            <div className="card-image">
              <img src={InstitutionalCollegiumImage} alt="Institutional Collegium" style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }} />
              <div className="card-overlay"></div>
              <div className="card-icon">
                <i className="fas fa-building"></i>
              </div>
            </div>
            <h3>INSTITUTIONAL COLLEGIUM</h3>
            <p className="card-description">
              Institutional Membership is open to all compaines. This membership provides access to all BVA workshops and events, offering corporations the opportunity to enhance their financial, valuation, and strategic decision-making capabilities through exposure to industry best practices. Each Collegium can register multiple individuals (upto 5 members)
            </p>
            <div className="price">{getPrice("INSTITUTIONAL COLLEGIUM")}</div>
            <Link to="/register" className="btn btn-primary" style={{textDecoration: 'none', display: 'inline-block', textAlign: 'center', margin: '0 1.5rem 1.5rem'}}>GET MEMBERSHIP</Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <h2>WHAT OUR MEMBERS SAY</h2>
        <div className="testimonials-container">
          <div className="testimonial-card">
            <div className="testimonial-header">
              <div className="author-avatar">
                <i className="fas fa-user-circle"></i>
              </div>
              <div className="author-info">
                <h4>T V S Siva Prasad</h4>
                <div className="rating">
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                </div>
              </div>
            </div>
            <div className="testimonial-content">
              <div className="quote-icon">
                <i className="fas fa-quote-left"></i>
              </div>
              <div className="testimonial-text">
                <p className="testimonial-excerpt">
                  "I feel proud to be a part of BVA where sharing of knowledge in
                  valuation is the motto. The webinars conducted by BVA are of top
                  notch quality and I learned a lot from them..."
                </p>
                <div
                  className="full-testimonial"
                  style={{ display: "none" }}
                >
                  <p>
                    "My journey with Bangalore Valuers' Association (BVA) - A
                    senior office bearer of central committee of ICMAI advised me
                    to study Valuation course. A professional colleague advised me
                    to join the valuation classes, which had just started and
                    immediately joined the 50 hours mandatory coaching. Being
                    qualified as a Valuer had given me immense happiness and a
                    sense of recognition. I joined Bangalore Valuers' Association
                    (BVA) even before passing the valuation examination, on the
                    advice of the founding members of the BVA. I feel proud to be
                    a part of BVA where sharing of knowledge in valuation is the
                    motto of BVA. It's dedicated members and the Management
                    Committee take BVA to greater heights every passing year. The
                    webinars conducted by BVA are of top notch quality. Second
                    and Fourth Saturday mornings are BVA webinar days,
                    invariably. I doubt whether any webinar was not conducted by
                    BVA on the designated days. I learned a lot from the webinars
                    of BVA and my doubts are clarified by my senior members of
                    BVA. I recommended BVA to my qualified valuer friends and many
                    of them joined BVA. I wish BVA all success in its endeavors
                    to make it the Best Source of Knowledge in Valuation subjects."
                  </p>
                </div>
                <button
                  className="read-more-btn"
                  onClick={(e) => {
                    const card = e.target.closest(".testimonial-card");
                    const excerpt = card.querySelector(".testimonial-excerpt");
                    const full = card.querySelector(".full-testimonial");
                    const btn = e.target;

                    if (full.style.display === "none") {
                      excerpt.style.display = "none";
                      full.style.display = "block";
                      btn.textContent = "Read Less";
                    } else {
                      excerpt.style.display = "block";
                      full.style.display = "none";
                      btn.textContent = "Read More";
                    }
                  }}
                >
                  Read More
                </button>
              </div>
            </div>
          </div>

          <div className="testimonial-card">
            <div className="testimonial-header">
              <div className="author-avatar">
                <i className="fas fa-user-circle"></i>
              </div>
              <div className="author-info">
                <h4>CS Jeevika Poddar</h4>
                <div className="rating">
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                </div>
              </div>
            </div>
            <div className="testimonial-content">
              <div className="quote-icon">
                <i className="fas fa-quote-left"></i>
              </div>
              <div className="testimonial-text">
                <p className="testimonial-excerpt">
                  "Joining BVA was one of the best decisions I made. Their
                  regular webinars, updates, and knowledge-sharing sessions have
                  been very useful in building both my confidence and
                  understanding..."
                </p>
                <div
                  className="full-testimonial"
                  style={{ display: "none" }}
                >
                  <p>
                    "I got introduced to the field of valuation through an email
                    from ICSI about the Registered Valuer course. Since I've
                    always enjoyed working with numbers, the course immediately
                    caught my interest. With the Companies Act making valuation
                    reports compulsory in many situations, I felt this was a great
                    area to explore further. My Journey With Bangalore Valuers'
                    Association (BVA) - After I cleared the exam, a close friend
                    told me about BVA. Joining BVA was one of the best decisions
                    I made. It is one of the leading bodies in the valuation field
                    and has really helped me grow as a professional. Their regular
                    webinars, updates, and knowledge-sharing sessions have been
                    very useful in building both my confidence and understanding.
                    Being a part of the BVA community has been a blessing. It
                    connects valuers from all over the country and creates a space
                    where everyone is open to helping each other. Whether it's
                    clearing doubts or sharing practical tips, the support from
                    fellow members has always been amazing. I'm truly grateful to
                    be part of this group. It keeps me motivated, helps me stay
                    updated and has given me a strong sense of belonging in the
                    valuation field."
                  </p>
                </div>
                <button
                  className="read-more-btn"
                  onClick={(e) => {
                    const card = e.target.closest(".testimonial-card");
                    const excerpt = card.querySelector(".testimonial-excerpt");
                    const full = card.querySelector(".full-testimonial");
                    const btn = e.target;

                    if (full.style.display === "none") {
                      excerpt.style.display = "none";
                      full.style.display = "block";
                      btn.textContent = "Read Less";
                    } else {
                      excerpt.style.display = "block";
                      full.style.display = "none";
                      btn.textContent = "Read More";
                    }
                  }}
                >
                  Read More
                </button>
              </div>
            </div>
          </div>

          <div className="testimonial-card">
            <div className="testimonial-header">
              <div className="author-avatar">
                <i className="fas fa-user-circle"></i>
              </div>
              <div className="author-info">
                <h4>CA Prashant Karanth</h4>
                <div className="rating">
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                </div>
              </div>
            </div>
            <div className="testimonial-content">
              <div className="quote-icon">
                <i className="fas fa-quote-left"></i>
              </div>
              <div className="testimonial-text">
                <p className="testimonial-excerpt">
                  "BVA has been a very positive influence on my professional
                  career with enriching experiences. The group has high-thinking
                  individuals who are seasoned and experienced..."
                </p>
                <div
                  className="full-testimonial"
                  style={{ display: "none" }}
                >
                  <p>
                    "BVA has been a very Positive Influence on My professional
                    career with lot of Enriching experiences by Being passive
                    observer to lot of High quality Discussions in the Group. The
                    Group has lot of High Thinking Individuals who have are
                    seasoned and experienced in Handling lot of High Stake
                    assignments. The Weekend online Seminars have also been of
                    great value with Lot of High Quality Speakers enlightening the
                    Group with Their real life experiences. The best Part about
                    the group is lot of Learned individuals who are willing to
                    share their learnings without any expectations. One can say
                    Learning and Sharing is the Most important attribute of BVA.
                    Credit to the managing Committee and the members who are
                    making this group one of the best places to be."
                  </p>
                </div>
                <button
                  className="read-more-btn"
                  onClick={(e) => {
                    const card = e.target.closest(".testimonial-card");
                    const excerpt = card.querySelector(".testimonial-excerpt");
                    const full = card.querySelector(".full-testimonial");
                    const btn = e.target;

                    if (full.style.display === "none") {
                      excerpt.style.display = "none";
                      full.style.display = "block";
                      btn.textContent = "Read Less";
                    } else {
                      excerpt.style.display = "block";
                      full.style.display = "none";
                      btn.textContent = "Read More";
                    }
                  }}
                >
                  Read More
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <h2>FREQUENTLY ASKED QUESTIONS</h2>
        <div className="faq-container">
          <div className="faq-item">
            <h3>How do I apply for membership?</h3>
            <p>You can apply for membership by clicking on the "Get Membership" button for your preferred plan and following the application process.</p>
          </div>
          
          <div className="faq-item">
            <h3>What is the membership renewal process?</h3>
            <p>Existing members can renew their membership by clicking on the "Renew Membership" button and following the renewal process. Renewals offer up to 20% discount on all plans if done before the membership expires or as decided by the Committee from time to time.</p>
          </div>
          
          <div className="faq-item">
            <h3>When can I renew my membership?</h3>
            <p>Memberships are valid for one full session. You can typically start the renewal process up to 60 days before your current membership expires.</p>
          </div>
          
          <div className="faq-item">
            <h3>How is membership validity determined?</h3>
            <p>Memberships are valid for one full year or part year depending upon time of payment.</p>
          </div>
          
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>JOIN THE BVA COMMUNITY TODAY</h2>
          <p>Become part of a growing network of valuation professionals and enhance your career prospects.</p>
          <div className="cta-buttons">
            <Link to="/register" className="btn btn-large" style={{backgroundColor: "rgb(247, 201, 54)", textDecoration: 'none', display: 'inline-block', textAlign: 'center'}}>BECOME A MEMBER</Link>
            <button 
              className="btn btn-secondary btn-large"
              onClick={openRenewalModal}
            >
              RENEW MEMBERSHIP
            </button>
          </div>
        </div>
      </section>

      {/* Renewal Modal */}
      <RenewalModal />

      {/* Image Attribution Section */}
      <section className="attribution-section" style={{display: 'none'}}>
        <div className="attribution-content">
          <h3>Image Credits</h3>
          <p>All images used in this website are from <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer">Unsplash</a>, a platform that provides free-to-use, high-definition photos.</p>
          <p>Individual image credits have been provided where applicable.</p>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default MembershipPage;